import { useCustom } from "@refinedev/core";
import { AlertTriangle, Check, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ApiError, api } from "@/lib/api";
import { errorMessage } from "@/lib/format";
import type { Product, Serial, SerialRecard } from "@/lib/providers";
import { type RecardOutcome, outcome, refusalMessage } from "@/lib/recard";

/**
 * ОКНО ПЕРЕСОРТИЦЫ — «вещь лежит не тем размером, переведите её на нужный».
 *
 * На полке 48-й, а в учёте 44-й: отберут его по чужому ярлыку, увезут на
 * маркетплейс чужим баркодом и вернут пересортом. До этого окна исправить
 * такое можно было только в CRM руками.
 *
 * РАЗМЕРЫ ПРИЕЗЖАЮТ ИЗ РАЗДЕЛА «ТОВАРЫ» (`GET /api/products/{model_id}`) — той
 * же дверью, что рисует карточку товара, и второй двери ради списка заводить
 * не стали: размеры модели это один ответ на один вопрос, и разойдись они,
 * «какие бывают размеры» считалось бы на складе двумя способами. Оттуда же
 * едет остаток каждого размера — подсказка «сколько таких у нас есть».
 *
 * ОКНО, А НЕ КЛИК ПО СТРОКЕ: правка уходит в боевой учёт под подписью того,
 * кто вошёл, и меняет то, каким товаром вещь поедет на маркетплейс. Промах
 * пальцем не должен этого значить — то же решение, что у окна списания.
 *
 * ЧУЖАЯ МОДЕЛЬ ЗДЕСЬ НЕДОСТУПНА ВОВСЕ. Окно показывает размеры ОДНОЙ модели —
 * той, которой вещь уже числится, — поэтому `model_change` дверь отсюда не
 * получает никогда. Смена товара обнуляет девять полей описания вещи; её
 * просят иначе и по другому поводу, и ставить её рядом с обычным пересортом
 * значило бы дать сделать необратимое одним лишним щелчком.
 */
export function RecardSheet({
  item,
  onClose,
  onDone,
}: {
  item: Serial;
  onClose: () => void;
  /** Правка состоялась — экран надо перечитать: вещь стала другим размером. */
  onDone: () => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  const [done, setDone] = useState<RecardOutcome | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const model = item.card?.model_id ?? 0;
  const product = useCustom<Product>({
    url: `/api/products/${model}`,
    method: "get",
    queryOptions: { enabled: model > 0 },
  });
  const cards = product.result.data?.cards ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const target = cards.find((one) => one.card_id === picked);

  async function send() {
    if (!target) return;
    setBusy(true);
    setFailed("");
    try {
      const reply = await api.post<SerialRecard>(
        `/api/serial/${item.serial}/card`,
        // Карточка названа НОМЕРОМ: размеры уже на экране списком, и баркод
        // здесь был бы вторым способом сказать то же самое. Баркод принимает
        // та же дверь — его шлёт тот, у кого в руках сканер, а не список.
        { card_id: target.card_id },
      );
      const said = outcome(reply, target.size);
      setDone(said);
      if (said.reread) onDone();
    } catch (err) {
      setFailed(
        err instanceof ApiError ? refusalMessage(err.message) : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Смена размера"
      data-annot="окно пересортицы"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-full w-full max-w-[460px] flex-col gap-3 overflow-y-auto rounded-[var(--radius-card)] border border-border bg-bg-elevated p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[14px] font-semibold">
            Размер вещи
            <span className="tnum ml-1.5 text-[12.5px] font-normal text-fg-muted">
              серия {item.serial}
            </span>
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            data-annot="закрыть"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-[var(--radius-ui)] border border-border text-fg-muted hover:text-fg"
          >
            <X size={14} />
          </button>
        </div>

        {/* ЧЕМ ВЕЩЬ ЧИСЛИТСЯ СЕЙЧАС — с фотографией и баркодом: человек держит
            вещь в руках и сверяет с ней именно это. */}
        <div
          data-annot="товар"
          className="flex items-center gap-3 rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2"
        >
          <div className="flex h-[54px] w-[40px] flex-none items-center justify-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg text-[10px] text-fg-muted">
            {item.card?.image_url ? (
              <img
                src={item.card.image_url}
                alt={item.card.name}
                referrerPolicy="no-referrer"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="tnum">нет</span>
            )}
          </div>
          <div className="min-w-0">
            <p className="tnum text-[13px] font-medium">
              {item.card?.size ?? item.size ?? "—"}
            </p>
            <p className="truncate text-[12.5px] text-fg-muted">
              {item.card?.name ?? "—"}
            </p>
            <p className="tnum text-[12px] text-fg-muted">{item.card?.barcode}</p>
          </div>
        </div>

        {done ? (
          <Result outcome={done} onClose={onClose} />
        ) : (
          <>
            <p className="text-[13px]">На какой размер перевести</p>

            {product.query.isFetching && cards.length === 0 ? (
              <p className="text-[12.5px] text-fg-muted">загружаю размеры…</p>
            ) : null}

            {/* Все размеры модели, включая текущий: он помечен и не выбирается.
                Убери его из списка — пропал бы ответ на «а какой сейчас», и
                человеку пришлось бы держать его в голове. */}
            <div className="flex flex-col gap-1" data-annot="размеры">
              {cards.map((one) => {
                const now = one.card_id === item.card?.card_id;
                return (
                  <button
                    key={one.card_id}
                    type="button"
                    disabled={now}
                    onClick={() => setPicked(one.card_id)}
                    data-annot="размер"
                    className={`flex items-center justify-between gap-3 rounded-[var(--radius-ui)] border px-2.5 py-1.5 text-left text-[13px] ${
                      now
                        ? "border-border bg-bg-subtle text-fg-muted"
                        : one.card_id === picked
                          ? "border-border-strong bg-bg-hover"
                          : "border-border hover:bg-bg-hover"
                    }`}
                  >
                    <span className="tnum font-medium">{one.size}</span>
                    <span className="tnum truncate text-[12px] text-fg-muted">
                      {one.barcode}
                    </span>
                    <span className="flex-none text-[12px] text-fg-muted">
                      {now ? "сейчас" : `${one.units + one.units_salon} шт`}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Сказано ДО нажатия: правка уходит в боевой учёт и меняет то,
                каким товаром вещь поедет на маркетплейс. */}
            <p className="flex items-start gap-2 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2.5 py-2 text-[12px] text-fg-muted">
              <AlertTriangle size={14} className="mt-px flex-none" />
              <span>
                Вещь станет числиться другим размером под вашей подписью. Размер,
                цвет и баркод пересчитает CRM — вернуть прежний можно только
                такой же заменой.
              </span>
            </p>

            {failed ? (
              <p
                role="alert"
                data-annot="отказ"
                className="rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-2.5 py-2 text-[12.5px]"
              >
                {failed}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={send}
                disabled={busy || !target}
                data-annot="перевести"
                className="h-8 rounded-[var(--radius-ui)] border border-border-strong bg-bg-hover px-3 text-[13px] disabled:opacity-50"
              >
                {busy ? "Меняю…" : "Перевести"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Что получилось: строка итога и поля, которые правка задела. */
function Result({
  outcome,
  onClose,
}: {
  outcome: RecardOutcome;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col gap-2" data-annot="итог">
      <p className="flex items-start gap-2 text-[13px]">
        <Check size={14} className="mt-0.5 flex-none text-fg-muted" />
        <span>{outcome.head}</span>
      </p>
      {outcome.lines.length ? (
        <ul className="flex flex-col gap-0.5 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2.5 py-2 text-[12px] text-fg-muted">
          {outcome.lines.map((line) => (
            <li key={line} className="tnum">
              {line}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}
